import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { getRole, setSession } from './middleware/auth';
import Nav from './components/Nav';
import Overview from './pages/Overview';
import LabWorkstation from './pages/LabWorkstation';
import Admin from './pages/Admin';
import Contacts from './pages/Contacts';
import Settings from './pages/Settings';
import Pipeline from './pages/Pipeline';
import Cases from './pages/Cases';
import ClientLogin from './pages/ClientLogin';
import ClientPortal from './pages/ClientPortal';
import IntakeStandalone from './pages/IntakeStandalone';
import HockeyIntelligence from './pages/HockeyIntelligence';

const mono = { fontFamily: "'DM Mono', monospace" };
const sans = { fontFamily: "'DM Sans', sans-serif" };

const CLIENT_PATHS = ['/client-login', '/portal', '/intake'];

// ── Dashboard placeholder (WC fills) ────────────────────────────────────

function Dashboard() {
  return (
    <div style={{ padding: 40 }}>
      <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#7A5230', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 20, height: 1, background: '#7A5230', flexShrink: 0 }} />
        DASHBOARD
      </div>
      <div style={{ ...mono, fontSize: 11, color: '#7A94A8', letterSpacing: '0.14em' }}>LOADING...</div>
    </div>
  );
}

// ── Route wrappers ──────────────────────────────────────────────────────

function OverviewRoute() {
  const { engagement_id } = useParams();
  return <Overview engagementId={engagement_id} />;
}

function LabRoute() {
  const { engagement_id } = useParams();
  return <LabWorkstation engagementId={engagement_id} engagements={[]} />;
}

// ── Advisor Login Screen ────────────────────────────────────────────────

function LoginScreen() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const analystPw = process.env.REACT_APP_ANALYST_PASSWORD;
    if (analystPw && password === analystPw) {
      setSession('analyst', null, '');
      localStorage.setItem('cordis_advisor_role', 'analyst');
      window.location.href = '/pipeline';
      return;
    }

    fetch('/api/advisor/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        setSubmitting(false);
        if (ok) {
          const role = data.role || 'advisor';
          localStorage.setItem('cordis_advisor_role', role);
          setSession(role, null, data.token || '');
          window.location.href = '/pipeline';
        } else {
          setError('Invalid credentials. Check your username and password.');
        }
      })
      .catch(() => {
        setSubmitting(false);
        setError('Connection failed. Try again.');
      });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#FAFAF8' }}>
      <form onSubmit={handleLogin} style={{ width: 360, padding: 40 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ ...mono, fontSize: 10, letterSpacing: '0.28em', color: '#7A5230', textTransform: 'uppercase', marginBottom: 8 }}>
            CORDIS GROUP
          </div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, color: '#0A1828' }}>
            MRI Workspace
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7A94A8', marginBottom: 4 }}>USERNAME</div>
          <input value={username} onChange={e => setUsername(e.target.value)}
            style={{ ...sans, fontSize: 14, padding: '12px 14px', border: '1px solid rgba(7,25,42,0.10)', width: '100%', outline: 'none', background: '#FFFFFF', minHeight: 48 }}
            autoFocus />
        </div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7A94A8', marginBottom: 4 }}>PASSWORD</div>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            style={{ ...sans, fontSize: 14, padding: '12px 14px', border: '1px solid rgba(7,25,42,0.10)', width: '100%', outline: 'none', background: '#FFFFFF', minHeight: 48 }} />
        </div>
        {error && <div style={{ ...mono, fontSize: 10, color: 'rgba(160,40,40,0.85)', marginBottom: 16 }}>{error}</div>}
        <button type="submit" disabled={submitting}
          style={{ ...mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
            background: '#0A1828', color: '#FFFFFF', border: 'none', padding: '14px 28px',
            cursor: submitting ? 'not-allowed' : 'pointer', width: '100%', minHeight: 48,
            opacity: submitting ? 0.6 : 1 }}>
          {submitting ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

// ── Advisor Layout ──────────────────────────────────────────────────────

function AdvisorLayout({ advisorName, advisorRole }) {
  return (
    <>
      <Nav advisorName={advisorName} advisorRole={advisorRole} />
      <main className="main-content" style={{ flex: 1, background: '#FAFAF8', minHeight: '100vh' }}>
        <style>{`
          .main-content { margin-left: 220px; }
          @media (max-width: 768px) { .main-content { margin-left: 0; margin-top: 52px; } }
        `}</style>
        <Routes>
          <Route path="/" element={<Navigate to="/pipeline" replace />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/cases" element={<Cases />} />
          <Route path="/overview/:engagement_id" element={<OverviewRoute />} />
          <Route path="/lab/:engagement_id" element={<LabRoute />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/hockey" element={<HockeyIntelligence />} />
          <Route path="/login" element={<LoginScreen />} />
        </Routes>
      </main>
    </>
  );
}

// ── Router ──────────────────────────────────────────────────────────────

function AppRouter({ advisorName, advisorRole, needsLogin }) {
  const location = useLocation();
  const isClientRoute = CLIENT_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  if (isClientRoute) {
    return (
      <Routes>
        <Route path="/client-login" element={<ClientLogin />} />
        <Route path="/portal" element={<ClientPortal />} />
        <Route path="/intake" element={<IntakeStandalone />} />
      </Routes>
    );
  }

  if (needsLogin) return <LoginScreen />;

  return <AdvisorLayout advisorName={advisorName} advisorRole={advisorRole} />;
}

// ── App ─────────────────────────────────────────────────────────────────

export default function App() {
  const [advisorName, setAdvisorName] = useState('');
  const [advisorRole, setAdvisorRole] = useState(
    () => localStorage.getItem('cordis_advisor_role') || ''
  );
  const [needsLogin, setNeedsLogin] = useState(false);

  useEffect(() => {
    fetch('/api/advisor/engagements', { credentials: 'include' })
      .then(r => {
        if (r.status === 401) {
          setNeedsLogin(true);
          return [];
        }
        return r.json();
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setNeedsLogin(false);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <AppRouter
        advisorName={advisorName}
        advisorRole={advisorRole}
        needsLogin={needsLogin}
      />
    </BrowserRouter>
  );
}
